import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Phone, Shield, Droplets } from "lucide-react";
import { Link } from "react-router-dom";

const Emergency = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Emergency Protocols" 
        description="What to do when things go wrong on a job site"
      >
        <section className="space-y-8">
          {/* Emergency Contacts */}
          <div className="bg-destructive/10 border-2 border-destructive rounded-lg p-6">
            <h2 className="text-xl font-semibold text-destructive mb-4 flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Emergency Contacts
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-background rounded-lg p-4 border">
                <p className="font-semibold text-foreground">BlueLux Office</p>
                <p className="text-muted-foreground">For all service emergencies</p>
                <p className="text-sm text-muted-foreground mt-1">Contact through app or call during business hours</p>
              </div>
              <div className="bg-background rounded-lg p-4 border">
                <p className="font-semibold text-foreground">911</p>
                <p className="text-muted-foreground">Life-threatening emergencies only</p>
                <p className="text-sm text-muted-foreground mt-1">Injury, fire, medical emergency</p>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              For the complete emergency contact list, see{" "}
              <Link to="/wiki/resources/contacts" className="text-primary hover:underline">
                Emergency Contacts
              </Link>
            </p>
          </div>

          {/* Chemical Exposure */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Shield className="h-6 w-6 text-primary" />
              Chemical Exposure Response
            </h2>
            
            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Skin Contact</h4>
                <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Immediately rinse affected area with plenty of water</li>
                  <li>Continue rinsing for at least 15-20 minutes</li>
                  <li>Remove contaminated clothing while rinsing</li>
                  <li>Do not apply creams or ointments</li>
                  <li>Seek medical attention if irritation persists</li>
                </ol>
              </div>
              
              <div className="bg-card border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Eye Contact</h4>
                <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Immediately flush eyes with clean water</li>
                  <li>Hold eyelids open and rinse for at least 15 minutes</li>
                  <li>Remove contact lenses if present (after initial flushing)</li>
                  <li>Seek medical attention immediately</li>
                </ol>
                <div className="bg-destructive/10 rounded-lg p-3 mt-4">
                  <p className="text-sm text-muted-foreground">
                    <AlertTriangle className="h-4 w-4 text-destructive inline mr-1" />
                    <strong>Do not rub eyes.</strong> Rubbing can cause additional damage.
                  </p>
                </div>
              </div>
              
              <div className="bg-card border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Inhalation</h4>
                <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Move to fresh air immediately</li>
                  <li>If difficulty breathing, call 911</li>
                  <li>Loosen tight clothing</li>
                  <li>If not breathing, perform CPR if trained</li>
                  <li>Seek medical attention for any respiratory symptoms</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Chemical Spill */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Droplets className="h-6 w-6 text-primary" />
              Chemical Spill Containment
            </h2>
            
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Small Spills (Less than 1 gallon)</h4>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Wear gloves and eye protection</li>
                <li>Contain the spill with absorbent material if available</li>
                <li>For acid spills: neutralize with baking soda or soda ash</li>
                <li>For chlorine spills: dilute with large amounts of water</li>
                <li>Clean up and dispose of properly</li>
                <li>Document the incident</li>
              </ol>
            </div>
            
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 mt-4">
              <h4 className="font-semibold text-destructive mb-3">Large Spills</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Evacuate the immediate area</li>
                <li>• Do NOT attempt to clean up large chemical spills yourself</li>
                <li>• Call the office immediately</li>
                <li>• Keep others away from the area</li>
                <li>• If fumes are present, stay upwind</li>
                <li>• Wait for professional assistance</li>
              </ul>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Never mix chemicals!</strong> Mixing chlorine with acid 
                  creates toxic chlorine gas. If chemicals have mixed, evacuate immediately and call for help.
                </p>
              </div>
            </div>
          </div>

          {/* Equipment Failure */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Equipment Failure</h2>
            
            <div className="space-y-4">
              <div className="bg-card border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Major Leak Discovered</h4>
                <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Turn off the pump immediately if safe to do so</li>
                  <li>Take photos documenting the leak</li>
                  <li>Notify the office immediately</li>
                  <li>If water is reaching electrical equipment, do NOT touch</li>
                  <li>Alert client if home and it's a significant issue</li>
                </ol>
              </div>
              
              <div className="bg-card border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Pump Failure</h4>
                <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                  <li>Check breaker first — may be tripped</li>
                  <li>Look for obvious issues (debris, overheating)</li>
                  <li>Do not repeatedly try to restart if it keeps shutting off</li>
                  <li>Document the issue with photos</li>
                  <li>Notify office for repair scheduling</li>
                </ol>
              </div>
              
              <div className="bg-card border rounded-lg p-6">
                <h4 className="font-semibold text-foreground mb-3">Electrical Issues</h4>
                <ul className="space-y-2 text-muted-foreground">
                  <li>• Do NOT touch equipment if you see sparks, smell burning, or hear buzzing</li>
                  <li>• If safe, turn off power at the breaker</li>
                  <li>• Photograph the issue</li>
                  <li>• Notify office immediately</li>
                  <li>• A licensed electrician may be required</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Property Damage */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Property Damage</h2>
            
            <div className="bg-card border rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                If you accidentally cause damage to client property (equipment, landscaping, property, vehicle, etc.):
              </p>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Stop what you're doing</li>
                <li>Assess the damage — don't make it worse</li>
                <li>Take photos documenting exactly what happened</li>
                <li>Notify the office immediately — do not wait</li>
                <li>Be honest about what occurred</li>
                <li>If the client is home, inform them professionally</li>
                <li>Do not attempt repairs outside your expertise</li>
              </ol>
              <div className="bg-primary/10 rounded-lg p-3 mt-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Remember:</strong> Accidents happen. Reporting quickly 
                  and honestly is always the right choice. BlueLux carries insurance for these situations.
                </p>
              </div>
            </div>
          </div>

          {/* Injury */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Injury on Job Site</h2>
            
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">If You Are Injured</h4>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Stop work immediately</li>
                <li>For serious injury, call 911</li>
                <li>For minor injury, administer first aid</li>
                <li>Notify the office as soon as possible</li>
                <li>Document what happened (photos, notes)</li>
                <li>Seek medical attention if needed</li>
              </ol>
            </div>
            
            <div className="bg-card border rounded-lg p-6 mt-4">
              <h4 className="font-semibold text-foreground mb-3">If You Witness an Emergency at Client Property</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Call 911 for any life-threatening situation</li>
                <li>• Stay on scene if safe until help arrives</li>
                <li>• Notify the office immediately after</li>
                <li>• Provide accurate information to emergency responders</li>
              </ul>
            </div>
          </div>

          {/* Escalation */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">When to Escalate</h2>
            <div className="bg-card border-2 border-primary rounded-lg p-6">
              <p className="text-muted-foreground mb-4">
                Always contact the office immediately for:
              </p>
              <ul className="space-y-2 text-muted-foreground">
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Any injury (to yourself or others)</li>
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Chemical exposure beyond minor splash</li>
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Property damage</li>
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Major equipment failure</li>
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Client confrontation or complaint</li>
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Safety hazards (electrical, structural)</li>
                <li><AlertTriangle className="h-4 w-4 text-destructive inline mr-2" />Anything that doesn't feel right</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-4 font-medium">
                When in doubt, call the office. It's always better to ask than to guess.
              </p>
            </div>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Emergency;